import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Heart,
  Users,
  Shield,
  Building2,
  Video,
  Activity,
  Calculator,
  Clock,
  DollarSign,
  CheckCircle,
  Sparkles
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const TratamientosHubPage = () => {
  const treatmentTypes = [
    {
      name: "Tratamiento Ambulatorio",
      description: "Tratamiento sin internación. Ideal para adicciones leves a moderadas, permite mantener trabajo y familia.",
      features: ["Sesiones 2-3 veces por semana", "Mantén tu rutina diaria", "Terapia individual y grupal"],
      icon: Building2,
      color: "from-blue-500 to-cyan-600",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      textColor: "text-blue-400",
      path: "/tratamiento/ambulatorio",
      price: "$300.000 - $800.000/mes",
      duration: "3-6 meses"
    },
    {
      name: "Tratamiento Residencial",
      description: "Internación completa en centro especializado. La opción más efectiva para adicciones severas.",
      features: ["Atención 24/7", "Ambiente controlado", "Múltiples enfoques terapéuticos"],
      icon: Shield,
      color: "from-emerald-500 to-teal-600",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
      textColor: "text-emerald-400",
      path: "/tratamiento/residencial",
      price: "$300.000 - $5.000.000/mes",
      duration: "1-12 meses"
    },
    {
      name: "Tratamiento Online",
      description: "Terapia a distancia mediante videollamada. Flexible y accesible desde cualquier lugar.",
      features: ["Desde tu hogar", "Horarios flexibles", "Mismo rigor terapéutico"],
      icon: Video,
      color: "from-purple-500 to-violet-600",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      textColor: "text-purple-400",
      path: "/tratamiento/online",
      price: "$50.000 - $120.000/sesión",
      duration: "3-12 meses"
    },
    {
      name: "Desintoxicación",
      description: "Fase inicial de tratamiento para eliminar sustancias del cuerpo de forma segura y supervisada.",
      features: ["Supervisión médica 24/7", "Manejo de síndrome de abstinencia", "Duración corta pero intensiva"],
      icon: Activity,
      color: "from-red-500 to-orange-600",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/30",
      textColor: "text-red-400",
      path: "/tratamiento/desintoxicacion",
      price: "$800.000 - $2.000.000",
      duration: "7-21 días"
    },
    {
      name: "Grupos de Apoyo AA/NA",
      description: "Comunidades de ayuda mutua basadas en los 12 pasos. Gratuitos y disponibles en todo Chile.",
      features: ["100% gratuito", "Apoyo entre pares", "Reuniones diarias disponibles"],
      icon: Users,
      color: "from-amber-500 to-yellow-600",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30",
      textColor: "text-amber-400",
      path: "/tratamiento/grupos-apoyo",
      price: "Gratuito",
      duration: "Continuo"
    },
    {
      name: "Centros Gratuitos",
      description: "Opciones de tratamiento sin costo a través de programas públicos y organizaciones sociales.",
      features: ["Sin costo", "SENDA y municipalidades", "Comunidades terapéuticas"],
      icon: Heart,
      color: "from-pink-500 to-rose-600",
      bgColor: "bg-pink-500/10",
      borderColor: "border-pink-500/30",
      textColor: "text-pink-400",
      path: "/tratamiento/gratis",
      price: "Gratuito",
      duration: "Variable"
    }
  ];

  const comparisonData = [
    { type: "Ambulatorio", severity: "Leve-Moderada", duration: "3-6 meses", cost: "$$$", effectiveness: "60-70%" },
    { type: "Residencial", severity: "Moderada-Severa", duration: "1-12 meses", cost: "$$$$", effectiveness: "70-85%" },
    { type: "Online", severity: "Leve-Moderada", duration: "3-12 meses", cost: "$$", effectiveness: "55-65%" },
    { type: "Desintoxicación", severity: "Todas", duration: "7-21 días", cost: "$$$", effectiveness: "Fase inicial" },
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Tipos de Tratamiento para Adicciones",
    "description": "Guía completa de tipos de tratamiento para adicciones: ambulatorio, residencial, online, desintoxicación y grupos de apoyo.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": treatmentTypes.map((treatment, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": treatment.name,
        "url": `https://sinadicciones.org${treatment.path}`
      }))
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tipos de Tratamiento para Adicciones: Guía Completa | Sin Adicciones Chile</title>
        <meta name="description" content="Conoce todos los tipos de tratamiento para adicciones: ambulatorio, residencial, online, desintoxicación y grupos de apoyo. Precios, duración y efectividad en Chile." />
        <meta property="og:title" content="Tipos de Tratamiento para Adicciones en Chile" />
        <meta property="og:description" content="Guía completa de opciones de tratamiento: desde ambulatorio hasta internación. Compara precios, duración y encuentra el adecuado." />
        <link rel="canonical" href="https://sinadicciones.org/tratamientos" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamientos" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Tratamientos</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Encuentra tu camino</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tipos de{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                Tratamiento
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Cada persona es diferente, y cada adicción requiere un enfoque personalizado. 
              Conoce las opciones de tratamiento disponibles y encuentra la más adecuada para tu situación.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/calculadora"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costos
              </Link>
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Centros
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Types Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Explora las Opciones de Tratamiento
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Desde tratamiento ambulatorio hasta internación completa. Encuentra el nivel de atención que necesitas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatmentTypes.map((treatment, index) => (
              <Link 
                key={index}
                to={treatment.path}
                className={`group relative overflow-hidden rounded-2xl ${treatment.bgColor} border ${treatment.borderColor} hover:border-opacity-60 transition-all duration-300`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${treatment.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-xl ${treatment.bgColor} flex items-center justify-center`}>
                      <treatment.icon className={`w-6 h-6 ${treatment.textColor}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold ${treatment.textColor} group-hover:brightness-110 transition-all`}>
                        {treatment.name}
                      </h3>
                    </div>
                    <ArrowRight className={`w-5 h-5 ${treatment.textColor} opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all`} />
                  </div>
                  
                  <p className="text-slate-400 text-sm mb-4">
                    {treatment.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    {treatment.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                        <CheckCircle className={`w-4 h-4 ${treatment.textColor} flex-shrink-0`} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-slate-700/50 grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300 text-xs">{treatment.price}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300 text-xs">{treatment.duration}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Comparativa de Tratamientos
            </h2>
            <p className="text-slate-400">
              Compara las principales opciones según severidad, duración y efectividad.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Tipo</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Severidad</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Duración</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Costo</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Efectividad</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors">
                    <td className="py-4 px-4 text-white font-medium">{row.type}</td>
                    <td className="py-4 px-4 text-slate-400">{row.severity}</td>
                    <td className="py-4 px-4 text-slate-400">{row.duration}</td>
                    <td className="py-4 px-4 text-emerald-400">{row.cost}</td>
                    <td className="py-4 px-4 text-slate-400">{row.effectiveness}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/calculadora"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium rounded-xl hover:bg-emerald-500/20 transition-all"
            >
              <Calculator className="w-5 h-5" />
              Usa nuestra calculadora para estimar costos personalizados
            </Link>
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              ¿Cómo elegir el tratamiento adecuado?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-emerald-400">Considera Ambulatorio si:</h3>
                <ul className="space-y-2">
                  {[
                    "Adicción leve a moderada",
                    "Entorno familiar estable",
                    "Necesitas mantener trabajo/estudios",
                    "Buen sistema de apoyo",
                    "Primera vez en tratamiento"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-red-400">Considera Residencial si:</h3>
                <ul className="space-y-2">
                  {[
                    "Adicción severa o de larga data",
                    "Entorno de consumo activo",
                    "Recaídas previas en ambulatorio",
                    "Comorbilidades psiquiátricas",
                    "Riesgo de daño a sí mismo u otros"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-700/50 text-center">
              <p className="text-slate-400 mb-4">¿No estás seguro? Nuestros asesores expertos te orientan en una sesión.</p>
              <Link 
                to="/asesoria"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-t from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Shield className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            El Primer Paso es el Más Importante
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            No importa qué tratamiento elijas, lo importante es comenzar. 
            Busca centros cerca de ti y da el primer paso hacia la recuperación.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/centros-de-rehabilitacion-drogas-chile"
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2 text-lg"
            >
              <Search className="w-5 h-5" />
              Buscar Centros
            </Link>
            <a 
              href="tel:1412"
              className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Fono Drogas: 1412
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TratamientosHubPage;
